import { useQueryClient } from "@tanstack/react-query";
import { PanelLeftClose } from "lucide-react";
import { useEffect, useId, useRef } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import {
  usePreviewHistoryQuery,
  type PreviewHistoryItem,
  type PreviewHistoryTraceStatus,
} from "@/api/previewHistory";
import type { PreviewManualSessionState } from "@/api/previewManual";
import { isSimRunTerminal, type PreviewSimRunStatus } from "@/api/previewSim";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { ISRAEL_TIME_NOTE, formatIsraelDateTime } from "@/lib/format";
import { labProgress, sortLabRuns } from "@/lib/labModel";
import type { LabSelection } from "@/lib/labUrl";
import { MANUAL_STATUS_LABELS } from "@/lib/previewManual";
import { SIM_STATUS_LABELS, describeEndReason } from "@/lib/previewSim";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { LabPanelToggle } from "./LabPanelToggle";
import { useLabManualStatuses } from "./useLabManualStatuses";
import { useLabSimStatuses } from "./useLabSimStatuses";

const TRACE_STATUS_LABELS = {
  waiting: "Waiting",
  live: "Live",
  completed: "Completed",
  incomplete: "Incomplete",
  unavailable: "Unavailable",
} as const satisfies Record<PreviewHistoryTraceStatus, string>;

/** LEFT pane (History): pick a run, optionally arm a second for comparison. */
export function LabRunsPane({
  projectId,
  selection,
  onSelect,
  compareArmed,
  onCollapse,
}: {
  projectId: string;
  selection: LabSelection;
  onSelect: (patch: Partial<LabSelection>) => void;
  /** Compare mode (set from the top bar): rows offer "Compare" to pick the second run. */
  compareArmed: boolean;
  /** Collapses the pane to its rail. */
  onCollapse?: () => void;
}) {
  const headingId = useId();
  const inspect = useCapability("preview.inspect");
  const queryClient = useQueryClient();
  const history = usePreviewHistoryQuery(projectId, {
    enabled: inspect.allowed,
  });
  const items = history.data?.pages.flatMap((page) => page.items) ?? [];
  const inspectable = items.filter(
    (item) => item.inspectionState === "inspectable",
  );
  const others = items.filter((item) => item.inspectionState !== "inspectable");
  const sims = useLabSimStatuses(
    projectId,
    inspectable.map((item) => item.previewRunRef),
  );
  const manuals = useLabManualStatuses(
    projectId,
    inspectable.map((item) => item.previewRunRef),
  );
  const runs = sortLabRuns(inspectable, sims);

  // A run seen unfinished that then ends refreshes the list once, so its row
  // (trace status, capture) is not left describing the moment it started.
  const unfinished = useRef(new Set<string>());
  useEffect(() => {
    let ended = false;
    for (const [ref, sim] of sims) {
      if (sim === null || sim === undefined) continue;
      if (!isSimRunTerminal(sim)) {
        unfinished.current.add(ref);
      } else if (unfinished.current.delete(ref)) {
        ended = true;
      }
    }
    if (ended) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.previewHistory(projectId),
      });
    }
  }, [sims, queryClient, projectId]);

  return (
    <section aria-labelledby={headingId}>
      <div className="flex h-14 items-center justify-between gap-2 border-b border-border px-4">
        <h2 id={headingId} className="text-base font-semibold">
          History
        </h2>
        {onCollapse !== undefined ? (
          <LabPanelToggle
            label="Collapse history"
            icon={PanelLeftClose}
            onClick={onCollapse}
          />
        ) : null}
      </div>

      <div className="space-y-3 p-4">
        {history.isPending ? (
          <QueryPendingState
            isPaused={history.isPaused}
            failureReason={history.failureReason}
            label="Loading runs"
            errorTitle="Runs could not be loaded"
            rows={3}
            queryKey={queryKeys.previewHistory(projectId)}
          />
        ) : !history.data ||
          (history.isError && isTerminalQueryError(history.error)) ? (
          <QueryErrorState
            error={history.error}
            subject="Preview runs"
            title="Runs could not be loaded"
            deniedDescription="This Project's Preview runs are not available to your account in this environment."
            treatNotFoundAsDenied
            onRetry={() => void history.refetch()}
          />
        ) : (
          <>
            <StaleDataNotice
              visible={history.isRefetchError && !history.isFetchNextPageError}
              updatedAt={history.dataUpdatedAt}
              onRetry={() => void history.refetch()}
            />
            {items.length === 0 ? (
              <EmptyState
                title="No conversations yet"
                description="Start one from the settings bar. It appears here as soon as the Editor records it."
              />
            ) : (
              <ol className="space-y-2">
                {runs.map((item) => (
                  <RunRow
                    key={item.previewRunRef}
                    item={item}
                    sim={sims.get(item.previewRunRef)}
                    manual={manuals.get(item.previewRunRef)}
                    selected={selection.run === item.previewRunRef}
                    compared={selection.compare === item.previewRunRef}
                    compareArmed={compareArmed}
                    onSelect={onSelect}
                  />
                ))}
                {others.map((item) => (
                  <li
                    key={item.previewRunRef}
                    className="rounded-md border border-border p-3 text-sm text-muted-foreground"
                  >
                    <span className="block font-medium">
                      Not available as a run
                    </span>
                    <span className="block">
                      {item.inspectionState === "temporarily_unavailable"
                        ? "The recorded target cannot confirm this run now."
                        : "The Editor recorded a dispatch attempt, but the target did not confirm a run."}{" "}
                      <time
                        dateTime={item.createdAt}
                        dir="ltr"
                        title={ISRAEL_TIME_NOTE}
                      >
                        {formatIsraelDateTime(item.createdAt)}
                      </time>
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {history.isFetchNextPageError ? (
              <p role="alert" className="text-sm text-destructive">
                Older Preview runs could not be loaded. The rows above are
                unchanged.
              </p>
            ) : null}
            {history.hasNextPage ? (
              <Button
                variant="outline"
                size="sm"
                disabled={history.isFetchingNextPage}
                onClick={() => void history.fetchNextPage()}
              >
                {history.isFetchingNextPage
                  ? "Loading older runs…"
                  : history.isFetchNextPageError
                    ? "Try loading older runs again"
                    : "Show older runs"}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function RunRow({
  item,
  sim,
  manual,
  selected,
  compared,
  compareArmed,
  onSelect,
}: {
  item: PreviewHistoryItem;
  sim: PreviewSimRunStatus | null | undefined;
  manual: PreviewManualSessionState | null | undefined;
  selected: boolean;
  compared: boolean;
  compareArmed: boolean;
  onSelect: (patch: Partial<LabSelection>) => void;
}) {
  const persona = manual
    ? "Manual session"
    : sim === undefined
      ? "Loading…"
      : sim === null
        ? "Manual preview"
        : sim.personaId;
  const statusLabel = sim
    ? SIM_STATUS_LABELS[sim.status]
    : manual
      ? MANUAL_STATUS_LABELS[manual.status]
      : item.traceStatus
        ? TRACE_STATUS_LABELS[item.traceStatus]
        : null;
  const ended = sim ? isSimRunTerminal(sim) : false;
  const createdAt = formatIsraelDateTime(item.createdAt);

  return (
    <li
      className={
        "rounded-md border p-2 " +
        (selected ? "border-primary bg-secondary" : "border-border bg-card")
      }
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => onSelect({ run: item.previewRunRef })}
        className="block w-full rounded-sm p-1 text-start focus-visible:outline-2 focus-visible:outline-ring"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span dir="auto" className="min-w-0 truncate text-sm font-medium">
            {persona}
          </span>
          {sim ? (
            <span dir="ltr" className="text-xs text-muted-foreground">
              {labProgress(sim)}
            </span>
          ) : null}
          {manual ? <Badge tone="accent">Manual</Badge> : null}
          {manual ? (
            <span dir="ltr" className="text-xs text-muted-foreground">
              {manual.turnsCompleted} turns
            </span>
          ) : null}
          {statusLabel !== null ? (
            <Badge tone={sim?.status === "failed" ? "destructive" : "outline"}>
              {statusLabel}
            </Badge>
          ) : null}
        </span>
        {ended && sim ? (
          <span className="block text-xs text-muted-foreground">
            {describeEndReason(sim.endReason)}
          </span>
        ) : null}
        <time
          dateTime={item.createdAt}
          dir="ltr"
          title={ISRAEL_TIME_NOTE}
          className="block text-xs text-muted-foreground"
        >
          {createdAt}
        </time>
      </button>
      {compared ? (
        <Badge tone="accent" className="mt-1">
          Compared
        </Badge>
      ) : compareArmed && !selected ? (
        <Button
          variant="outline"
          size="sm"
          className="mt-1"
          aria-label={`Compare with ${persona}, started ${createdAt}`}
          onClick={() => onSelect({ compare: item.previewRunRef })}
        >
          Compare
        </Button>
      ) : null}
    </li>
  );
}
